// This component is branched off from the platform InlineFeedback component that's WIP
// https://git.hubteam.com/HubSpot/ai-components-ui-library/tree/master/ai-components-ui-library/static/js/components/feedback
//
// Features that are added to this component are:
// Allow both positive & negative pop up forms (Both are optional)
// Allow consumer to configure options within each of the form
// Allow consumer to configure each form header text
// Allow consumer to configure copy tooltip text
// Allow consumer to configure positive & negative button tooltip
// Allow consumer to handle / differentiate callbacks for clicking on positive & negative buttons and submitting positive & negative form.

import { useCallback, useState } from 'react';
import { isFunction } from './internal/typeUtils';
import CopyMessageActionButton from './internal/CopyMessageActionButton';
import FeedbackButton from './internal/FeedbackButton';
import { trackInteraction as _trackInteraction, trackFeedbackCopyInteraction } from 'sales-enablement-lib/utils/usageTracker';
import { Flex } from 'foundations-components/components/Flex';
import { ThumbsDownIcon } from 'foundations-assets/icons/components/ThumbsDownIcon';
import { ThumbsUpIcon } from 'foundations-assets/icons/components/ThumbsUpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POSITIVE_FEEDBACK = 1;
const NEGATIVE_FEEDBACK = -1;

// TODO: Remove usageTracker in favor of usageTrackerEventNamespace
// when all tracking events have been migrated to Standalone Tracking
const FeedbackCopyButtons = ({
  hideCopyButton = false,
  copyDisabled = false,
  feedbackDisabled = false,
  copyMessage,
  onPositiveClick,
  onNegativeClick,
  onPositiveFormSubmit,
  onNegativeFormSubmit,
  usageTracker,
  negativeFeedbackOptions,
  positiveFeedbackOptions,
  freeformPlaceholder = 'dealSummary.feedback.form.freeformPlaceholderDefault',
  positiveFeedbackTooltipI18Key = `dealSummary.feedback.positiveFeedback.tooltipDefault`,
  negativeFeedbackTooltipI18Key = `dealSummary.feedback.negativeFeedback.tooltipDefault`,
  copyTooltipI18nKey = 'dealSummary.feedback.copy.tooltipDefault',
  positiveFormHeaderI18nKey = 'dealSummary.feedback.form.positiveHeaderDefault',
  negativeFormHeaderI18nKey = 'dealSummary.feedback.form.negativeHeaderDefault',
  usageTrackerEventNamespace = '',
  usageTrackerScreen = ''
}) => {
  const [draftVote, setDraftVote] = useState();
  const [negativeFeedbackPopoverOpen, setNegativeFeedbackPopoverOpen] = useState(false);
  const [positiveFeedbackPopoverOpen, setPositiveFeedbackPopoverOpen] = useState(false);
  const trackInteraction = useCallback(action => {
    return usageTrackerEventNamespace ? trackFeedbackCopyInteraction(usageTrackerEventNamespace, action, usageTrackerScreen) : _trackInteraction(action, usageTrackerScreen, usageTracker);
  }, [usageTracker, usageTrackerEventNamespace, usageTrackerScreen]);
  const handlePositiveFeedbackClick = useCallback(() => {
    const isPositiveFeedbackSelected = draftVote === POSITIVE_FEEDBACK;
    if (isPositiveFeedbackSelected) {
      setDraftVote(null);
      setPositiveFeedbackPopoverOpen(false);
      if (isFunction(onPositiveClick)) {
        // undo by reversing the vote
        onPositiveClick({
          vote: -1
        });
      }
      trackInteraction('click-upvote-undo');
    } else {
      setDraftVote(POSITIVE_FEEDBACK);
      setPositiveFeedbackPopoverOpen(true);
      setNegativeFeedbackPopoverOpen(false);
      if (isFunction(onPositiveClick)) {
        onPositiveClick({
          vote: 1
        });
      }
      trackInteraction('click-upvote');
    }
  }, [draftVote, onPositiveClick, trackInteraction]);
  const handleNegativeFeedbackClick = useCallback(() => {
    const isNegativeFeedbackSelected = draftVote === NEGATIVE_FEEDBACK;
    if (isNegativeFeedbackSelected) {
      setDraftVote(null);
      setNegativeFeedbackPopoverOpen(false);
      if (isFunction(onNegativeClick)) {
        // undo by reversing the vote
        onNegativeClick({
          vote: 1
        });
      }
      trackInteraction('click-downvote-undo');
    } else {
      setDraftVote(NEGATIVE_FEEDBACK);
      setNegativeFeedbackPopoverOpen(true);
      setPositiveFeedbackPopoverOpen(false);
      if (isFunction(onNegativeClick)) {
        onNegativeClick({
          vote: -1
        });
      }
      trackInteraction('click-downvote');
    }
  }, [draftVote, onNegativeClick, trackInteraction]);
  const handleClose = useCallback(() => {
    setNegativeFeedbackPopoverOpen(false);
    setPositiveFeedbackPopoverOpen(false);
  }, []);
  const handlePositiveSubmit = ({
    options,
    message: feedbackMessage
  }) => {
    setPositiveFeedbackPopoverOpen(false);
    if (isFunction(onPositiveFormSubmit)) {
      onPositiveFormSubmit({
        options,
        feedbackMessage,
        vote: POSITIVE_FEEDBACK
      });
    }
    trackInteraction('submit-positive-form');
  };
  const handleNegativeSubmit = ({
    options,
    message: feedbackMessage
  }) => {
    setNegativeFeedbackPopoverOpen(false);
    if (isFunction(onNegativeFormSubmit)) {
      onNegativeFormSubmit({
        options,
        feedbackMessage,
        vote: NEGATIVE_FEEDBACK
      });
    }
    trackInteraction('submit-negative-form');
  };
  return /*#__PURE__*/_jsxs(Flex, {
    "data-test-id": "inline-feedback",
    children: [/*#__PURE__*/_jsx(FeedbackButton, {
      featureTestId: "positive-feedback-button",
      feedbackDisabled: feedbackDisabled,
      feedbackFormHeaderI18nKey: positiveFormHeaderI18nKey,
      feedbackOptions: positiveFeedbackOptions,
      feedbackPopoverOpen: positiveFeedbackPopoverOpen,
      feedbackTooltipI18nKey: positiveFeedbackTooltipI18Key,
      freeformPlaceholder: freeformPlaceholder,
      handleClose: handleClose,
      handleFeedbackClick: handlePositiveFeedbackClick,
      handleFeedbackSubmit: handlePositiveSubmit,
      icon: ThumbsUpIcon,
      selected: draftVote === POSITIVE_FEEDBACK
    }), /*#__PURE__*/_jsx(FeedbackButton, {
      featureTestId: "negative-feedback-button",
      feedbackDisabled: feedbackDisabled,
      feedbackFormHeaderI18nKey: negativeFormHeaderI18nKey,
      feedbackOptions: negativeFeedbackOptions,
      feedbackPopoverOpen: negativeFeedbackPopoverOpen,
      feedbackTooltipI18nKey: negativeFeedbackTooltipI18Key,
      freeformPlaceholder: freeformPlaceholder,
      handleClose: handleClose,
      handleFeedbackClick: handleNegativeFeedbackClick,
      handleFeedbackSubmit: handleNegativeSubmit,
      icon: ThumbsDownIcon,
      selected: draftVote === NEGATIVE_FEEDBACK
    }), !hideCopyButton && /*#__PURE__*/_jsx(CopyMessageActionButton, {
      disabled: copyDisabled,
      message: copyMessage,
      usageTracker: usageTracker,
      tooltipI18nKey: copyTooltipI18nKey
    })]
  });
};
export default FeedbackCopyButtons;